import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import "./style.css";
import un from "../../Assests/Unstop.png";
import ParallaxBackground from "../ParallaxBackground/ParallaxBackground";

const REGISTER_URL =
  "https://unstop.com/internships/campus-ambassador-internship-udghosh-1728653?lb=use17Prx&utm_medium=Share&utm_source=WhatsApp";
const WHATSAPP_URL = "https://chat.whatsapp.com/D242QJ1etCkBplQQorMLNn?s=sw&p=i&mlu=4&amv=2";

/* ── Text mask-reveal animation helper ── */
const MaskReveal = ({ children, delay = 0, className = "" }) => (
  <div className="mask-parent">
    <motion.div
      className={`mask-child ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  </div>
);

/* ── Character-stagger reveal ── */
const CharReveal = ({ text, delay = 0, className = "" }) => (
  <motion.span
    className={className}
    initial="hidden"
    animate="visible"
    variants={{
      hidden: {},
      visible: { transition: { staggerChildren: 0.03, delayChildren: delay } },
    }}
  >
    {text.split("").map((ch, i) => (
      <motion.span
        key={i}
        style={{ display: "inline-block" }}
        variants={{
          hidden:  { opacity: 0, y: 6 },
          visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
        }}
      >
        {ch === " " ? "\u00A0" : ch}
      </motion.span>
    ))}
  </motion.span>
);

const Header = () => {
  /* Mouse parallax */
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const springX = useSpring(rawX, { stiffness: 45, damping: 22 });
  const springY = useSpring(rawY, { stiffness: 45, damping: 22 });
  const active = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => (active.current = true), 1800);
    const onMove = (e) => {
      if (!active.current) return;
      rawX.set((e.clientX / window.innerWidth - 0.5) * 2 * -7);
      rawY.set((e.clientY / window.innerHeight - 0.5) * 2 * -5);
    };
    window.addEventListener("mousemove", onMove);
    return () => { clearTimeout(t); window.removeEventListener("mousemove", onMove); };
  }, [rawX, rawY]);

  /* Scroll indicator animation ping */
  const [scrollVisible, setScrollVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setScrollVisible(true), 2200);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="hero-section" id="header">
      <ParallaxBackground />
      {/* ── Presented-by badge ── */}
      <motion.div
        className="hero-presented"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.7, ease: "easeOut" }}
      >
        <a href="https://unstop.com/" target="_blank" rel="noreferrer">
          <img src={un} alt="Unstop logo" className="hero-unstop-logo" />
        </a>
        <span className="hero-presents-text">Presents</span>
      </motion.div>

      {/* ── Main text panel ── */}
      <motion.div
        className="hero-text-panel"
        style={{ x: springX, y: springY }}
      >
        {/* Eyebrow */}
        <div className="hero-eyebrow-row">
          <CharReveal
            text="IIT KANPUR  •  UDGHOSH '26"
            delay={0.55}
            className="udg-eyebrow"
          />
        </div>

        {/* Main headline */}
        <h1 className="hero-headline">
          <MaskReveal delay={0.75}>BECOME THE</MaskReveal>
          <MaskReveal delay={0.87}>FACE OF</MaskReveal>
          <MaskReveal delay={0.99} className="hero-headline--gold font-display">
            UDGHOSH
          </MaskReveal>
        </h1>

        {/* Gold rule + descriptor */}
        <motion.div
          className="hero-desc-block"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="udg-gold-rule" />
          <p className="hero-desc udg-lede">
            Represent IIT Kanpur's largest sports festival.<br />
            Lead your campus. Build your network.<br />
            Earn exclusive rewards.
          </p>
        </motion.div>

        {/* Keyword strip */}
        <motion.div
          className="hero-keyword-strip"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 0.7 }}
        >
          <span>Advertise</span>
          <span className="hero-keyword-divider" />
          <span>Organize</span>
          <span className="hero-keyword-divider" />
          <span>Conduct</span>
          <span className="hero-keyword-divider" />
          <span>Coordinate</span>
        </motion.div>

        {/* Sub-headline */}
        <motion.p
          className="hero-subline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.7 }}
        >
          Be{" "}
          <span className="hero-subline--accent">CAMPUS AMBASSADOR</span>
          {" "}and represent your college in India's largest sports festival
        </motion.p>

        {/* CTA buttons */}
        <motion.div
          className="hero-ctas"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <a
            href={REGISTER_URL}
            className="udg-btn udg-btn-filled"
            target="_blank"
            rel="noreferrer"
            id="hero-register-btn"
          >
            Apply Now →
          </a>
          <a
            href={WHATSAPP_URL}
            className="udg-btn"
            target="_blank"
            rel="noreferrer"
            id="hero-whatsapp-btn"
          >
            Join WhatsApp
          </a>
        </motion.div>
      </motion.div>

      {/* ── Scroll indicator ── */}
      {scrollVisible && (
        <motion.div
          className="hero-scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <span className="hero-scroll-label">Scroll</span>
          <div className="udg-scroll-line">
            <motion.div
              className="udg-scroll-dot"
              animate={{ y: ["-100%", "240%"] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </motion.div>
      )}
    </section>
  );
};

export default Header;
